import _ from 'underscore';
import stgConfig from "../../../../config";
import axios from 'axios';

import Environment from '../../buyer-experience/common/environment';

const DEV_ENV = Environment.dev();
const STAGING_ENV = Environment.staging();
const PRODUCTION_ENV = Environment.production();

const BXP_EXPRESS_ENDPOINT = {};
BXP_EXPRESS_ENDPOINT[PRODUCTION_ENV] = 'https://www.atlassian.com/endpoint';
BXP_EXPRESS_ENDPOINT[STAGING_ENV] = `${window.location.hostname.indexOf('qa-wac') > -1 ? 'https://qa-wac.internal.atlassian.com' : stgConfig.stgWacInternalUrl}/endpoint`;
BXP_EXPRESS_ENDPOINT[DEV_ENV] = BXP_EXPRESS_ENDPOINT[STAGING_ENV];

const CONSENTHUB_ENDPOINT = {};
CONSENTHUB_ENDPOINT[PRODUCTION_ENV] = 'https://preferences.atlassian.com';
CONSENTHUB_ENDPOINT[STAGING_ENV] = 'https://preferences.stg.atlassian.com';
CONSENTHUB_ENDPOINT[DEV_ENV] = 'https://preferences.dev.atlassian.com';

class Client {

  /**
   *
   * @param {String} locale
   */
  constructor(options = {}) {

    this.config = {};
    this.options = options;

    const environment = Environment.getEnvironment();

    this.bxpExpressEndpoint = BXP_EXPRESS_ENDPOINT[environment];
    this.consentHubEndpoint = CONSENTHUB_ENDPOINT[environment];
  }

  /**
   *
   * @param {string} actionKey
   * @param {Object.<string, boolean>[]} actions
   *
   * @return {Object.<string, string|boolean>[]}
   */
  buildActionsList(actionKey, actions) {

    const targetKeys = Object.keys(actions);
    let actionsList = [];

    targetKeys.forEach((targetKey) => {
      const actionVal = actions[targetKey];
      let actionItem = {};
      actionItem['key'] = targetKey;
      actionItem[actionKey] = !!actionVal;

      actionsList.push(actionItem);
    });


    return actionsList;
  }

  /**
   * Build a list of consent actions formatted for the request.
   * Add stubbs for required consent docs.
   *
   * @param {Object.<string, string|boolean>[]} consents
   * @param {boolean} isUpdate - If "consent-update" is called, specific consent docs aren't enforced.
   *
   * @return {Object.<string, string|boolean>[]}
   */
  buildConsentsList(consents, isUpdate = false) {

    if (!isUpdate) {

      const requiredConsent = [
        'termsOfService',
        'privacyPolicy',
        'generalMarketingOptIn'
      ];

      _.each(requiredConsent, consentDoc => {
        if (!_.find(consents, { key: consentDoc })) {
          consents.push({
            key: consentDoc,
            displayedText: "'displayedText' not provided.",
            granted: false
          });
        }
      });
    }

    return consents;
  }

  /**
   *
   * @param {Object.<string, boolean>[]} subscriptions
   * @return {Object.<string, string|boolean>[]}
   */
  buildSubscriptionsList(subscriptions) {
    return this.buildActionsList('subscribed', subscriptions);
  }

  /**
   * Get "config" data from ConsentHub
   *
   * @return {Promise}
   */
  getConfig() {
    return this.requestConfig();
  }


  /**
   * @param {Object} params
   * @param {string} params.email
   * @param {string} params.formUrl
   * @param {string} params.locale
   * @param {string} params.site
   * @param {Object.<string, boolean>[]} params.consents
   *
   * @param {string} [params.providerId]
   * @param {Object.<string, boolean>[]} [params.subscriptions]
   *
   * @void
   */
  postConsentUpdate(params) {
    return this.postSubscriptions(params, true, true);
  }

  /**
   * Get "locale" data from ConsentHub
   *
   * @return {Promise}
   */
  getLocale() {
    return this.requestLocale();
  }

  /**
   * @param {Object} params
   * @param {string} params.email
   * @param {string} params.formUrl
   * @param {string} params.locale
   * @param {string} params.site
   * @param {Object.<string, boolean>[]} params.consents
   *
   * @param {string} [params.providerId]
   * @param {Object.<string, boolean>[]} [params.subscriptions]
   *
   * @void
   */
  postConsent(params) {
    return this.postSubscriptions(params, true);
  }

  /**
   * Intended for use by web forms that need to set a user's subscription status for a specific subscription list.
   *
   * @param {Object} params
   * @param {string} params.email
   * @param {string} params.formUrl
   * @param {string} params.locale
   * @param {string} params.site
   * @param {Object.<string, boolean>[]} params.subscriptions
   *
   * @param {string} [params.providerId]
   * @param {Object.<string, boolean>[]} [params.consents]
   *
   * @todo Refactor to eliminate this need.
   * @param {boolean} isConsentRequest - Both requests are almost identical. Avoiding code-duplication until we have a better solution.
   * @param {boolean} isConsentUpdate - This request is almost identical as well. Avoiding code-duplication until we have a better solution.
   *
   * @void
   */
  postSubscriptions(params, isConsentRequest = false, isConsentUpdate = false) {

    const action = isConsentRequest ? (isConsentUpdate ? 'consent-update' : 'consent') : 'subscriptions';

    let endpoint = `${this.bxpExpressEndpoint}/preferences/${action}`;
    const requiredProps = [
      'email',
      'site',
      'locale',
      'formUrl'
    ];

    if (isConsentRequest) {
      requiredProps.push('consents');
    } else {
      requiredProps.push('subscriptions');
    }

    // If a "locale" isn't provided, use the cached "locale" if available.
    if (!params.hasOwnProperty('locale') && this.config.hasOwnProperty('locale')) {
      params.locale = this.config.locale;
    }

    const isValidParams = _.isObject(params) && requiredProps.every((prop) => {
      return params.hasOwnProperty(prop);
    });

    if (!isValidParams) {
      throw `Invalid parameters. Required: {${requiredProps.toString()}}`;
    }

    const sourceData = params.source ? params.source : "WAC";
    const actionData = params.action ? params.action: "UPDATE_PREFERENCES";

    let data = {
      email: params.email,
      site: params.site,
      source: sourceData,
      action: actionData,
      locale: params.locale,
      formUrl: params.formUrl
    };

    if (params.hasOwnProperty('consents')) {
      data.consents = this.buildConsentsList(params.consents, isConsentUpdate);
    }

    if (params.hasOwnProperty('subscriptions')) {
      data.subscriptions = this.buildSubscriptionsList(params.subscriptions);
    }

    if (params.hasOwnProperty('subscriberDetails')) {
      data.subscriberDetails = params.subscriberDetails;
    }

    const requestConfig = {
      headers: {
        'Content-Type': 'application/json'
      }
    };
    return new Promise((resolve, reject) => {
      axios.post(endpoint, data, requestConfig)
        .then((response) => {
          resolve(response.status);
        })
        .catch((err) => {
          reject(err.toString());
        });
    });
  }

  /**
   * @return {Promise}
   */
  requestConfig() {

    return new Promise((resolve, reject) => {

      axios.post(`${this.bxpExpressEndpoint}/preferences/consent-config`)
        .then((response) => {
          this.config = response.data;
          resolve(response.data);
        })
        .catch(function (error) {
          reject(new Error(error));
        });
    });
  }

  /**
   * @return {Promise}
   */
  requestLocale() {
    return new Promise((resolve, reject) => {
      axios.post(`${this.bxpExpressEndpoint}/preferences/consent-config`)
        .then((response) => {
          this.config = response.data;
          resolve(response.data);
        })
        .catch(function (error) {
          reject(new Error(error));
        });
    });
  }
}

export default Client;
